import React from 'react';

export type WorkspaceSectionColor =
  | 'orange' | 'red' | 'blue' | 'slate' | 'violet' | 'amber' | 'sky' | 'rose' | 'indigo';

const BAR_CLASS: Record<WorkspaceSectionColor, string> = {
  orange: 'bg-orange-400',
  red: 'bg-red-400',
  blue: 'bg-blue-400',
  slate: 'bg-slate-300 dark:bg-slate-600',
  violet: 'bg-violet-300',
  amber: 'bg-amber-300',
  sky: 'bg-sky-300',
  rose: 'bg-rose-400',
  indigo: 'bg-indigo-300',
};

const TITLE_CLASS: Record<WorkspaceSectionColor, string> = {
  orange: 'text-orange-900 dark:text-orange-200',
  red: 'text-red-900 dark:text-red-200',
  blue: 'text-blue-900 dark:text-blue-200',
  slate: 'text-slate-800 dark:text-slate-100',
  violet: 'text-violet-900 dark:text-violet-200',
  amber: 'text-amber-900 dark:text-amber-200',
  sky: 'text-sky-900 dark:text-sky-200',
  rose: 'text-rose-900 dark:text-rose-200',
  indigo: 'text-indigo-900 dark:text-indigo-200',
};

const SUBTITLE_CLASS: Record<WorkspaceSectionColor, string> = {
  orange: 'text-orange-700 dark:text-orange-400',
  red: 'text-red-700 dark:text-red-400',
  blue: 'text-blue-700 dark:text-blue-400',
  slate: 'text-slate-500 dark:text-slate-400',
  violet: 'text-violet-700 dark:text-violet-400',
  amber: 'text-amber-700 dark:text-amber-400',
  sky: 'text-sky-700 dark:text-sky-400',
  rose: 'text-rose-700 dark:text-rose-400',
  indigo: 'text-indigo-700 dark:text-indigo-400',
};

const BADGE_CLASS: Record<WorkspaceSectionColor, string> = {
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  sky: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  rose: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
};

const ACTION_BUTTON_CLASS: Record<WorkspaceSectionColor, string> = {
  orange: 'border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-950/30',
  red: 'border-red-200 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/30',
  blue: 'border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/30',
  slate: 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800',
  violet: 'border-violet-200 text-violet-700 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-300 dark:hover:bg-violet-950/30',
  amber: 'border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-950/30',
  sky: 'border-sky-200 text-sky-700 hover:bg-sky-50 dark:border-sky-700 dark:text-sky-300 dark:hover:bg-sky-950/30',
  rose: 'border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30',
  indigo: 'border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/30',
};

export interface WorkspaceSectionProps {
  color: WorkspaceSectionColor;
  title: string;
  /** Badge de contagem ao lado do título. Omitido = sem badge. */
  count?: number;
  /** Texto de apoio abaixo do título, sem botão (ex.: "Aguardando o operador corrigir e reenviar"). */
  subtitle?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Omitido = seção sempre expandida (sem botão de recolher). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  children?: React.ReactNode;
}

/**
 * Uma seção dentro do bloco único de área de trabalho: faixa colorida à esquerda +
 * cabeçalho (título, contagem, ação, recolher) + conteúdo. Várias seções lado a lado
 * dentro de um `WorkspaceSectionGroup` formam o card único por perfil.
 */
const WorkspaceSection: React.FC<WorkspaceSectionProps> = ({
  color,
  title,
  count,
  subtitle,
  actionLabel,
  onAction,
  collapsed = false,
  onToggleCollapse,
  children,
}) => {
  return (
    <div className="flex">
      <div className={`w-1.5 shrink-0 ${BAR_CLASS[color]}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3 px-5 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-sm font-semibold ${TITLE_CLASS[color]}`}>{title}</span>
              {count != null && (
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${BADGE_CLASS[color]}`}>{count}</span>
              )}
            </div>
            {subtitle && (
              <p className={`mt-0.5 text-sm ${SUBTITLE_CLASS[color]}`}>{subtitle}</p>
            )}
          </div>
          <div className="ml-3 flex shrink-0 items-center gap-2">
            {actionLabel && onAction && (
              <button
                type="button"
                onClick={onAction}
                className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${ACTION_BUTTON_CLASS[color]}`}
              >
                {actionLabel}
              </button>
            )}
            {onToggleCollapse && (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="shrink-0 rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {collapsed ? 'Expandir' : 'Recolher'}
              </button>
            )}
          </div>
        </div>
        {!collapsed && children}
      </div>
    </div>
  );
};

export default WorkspaceSection;
